import { DroppableZone } from "@/lib/dnd/components/DroppableZone";
import { useQuestionHandlers } from "@/lib/dnd/hooks/useQuestionHandlers";
import { useLocalImageUpload } from "@/lib/hooks/slides/editor/useLocalImageUpload";
import { useAppDispatch } from "@/store/hooks";
import { updateElementProps } from "@/store/slideEditorSlice";
import { CANVAS_DEFAULT_FONT_FAMILY, DragMatchingImageStyles } from "@ai-teach/canvas-styles";
import type { DragMatchingImageElement } from "@ai-teach/types";
import { IconButton } from "@ai-teach/ui-web";
import { AiFillPicture } from "react-icons/ai";
import { HiPlus } from "react-icons/hi";
import { MdCheckBox, MdCheckBoxOutlineBlank } from "react-icons/md";
import { ChoiceButtons } from "./ChoiceButtons";
import { gradeChoice, gradedRingStyle } from "./gradedStyles";

interface DragMatchingImageRendererProps {
  element: DragMatchingImageElement;
  isLiveMode?: boolean;
  /** Index the student matched to the text in the play being replayed. */
  selectedImageId?: string | undefined;
  /** The question is over: the correct choice turns green whether or not it was picked. */
  isFrozen?: boolean | undefined;
}

export function DragMatchingImageRenderer({
  element,
  isLiveMode = false,
  selectedImageId,
  isFrozen,
}: DragMatchingImageRendererProps) {
  const dispatch = useAppDispatch();
  const { handleQuestionDragAndDrop } = useQuestionHandlers();
  const { handleFileDrop } = useLocalImageUpload((media, containerId) =>
    handleQuestionDragAndDrop(media, containerId)
  );

  const handleToggleIsCorrectAnswer = (e: React.MouseEvent, index: number) => {
    e.stopPropagation();

    if (index > element.imageChoices.length - 1) return;

    const updatedImageChoices = element.imageChoices.map((choice, i) => ({
      ...choice,
      isCorrectAnswer: i === index,
    }));

    dispatch(
      updateElementProps({
        id: element.id,
        props: {
          imageChoices: updatedImageChoices,
        },
      })
    );
  };

  const handleAddChoice = () => {
    if (element.imageChoices.length > 2) return;

    dispatch(
      updateElementProps({
        id: element.id,
        props: {
          imageChoices: [
            ...element.imageChoices,
            { media: { src: null, filename: null, id: null }, isCorrectAnswer: false },
          ],
        },
      })
    );
  };

  return (
    <div
      style={{
        ...DragMatchingImageStyles.outerDiv,
        borderRadius: element.border.cornerRadius,
        backgroundColor: element.backgroundColor ?? "transparent",
        pointerEvents: isLiveMode ? "none" : "auto",
      }}
      className="text-black"
    >
      {!isLiveMode && element.imageChoices.length < 3 && (
        <div onClick={handleAddChoice} className="absolute right-2 top-2 z-10">
          <IconButton
            aria-label="Add choice"
            variant="primary"
            shape="circle"
            size="sm"
            onClick={handleAddChoice}
          >
            <HiPlus className="w-5 h-5" aria-hidden="true" title="Add choice" />
          </IconButton>
        </div>
      )}

      <div
        contentEditable={!isLiveMode}
        data-no-moveable-drag
        suppressContentEditableWarning
        style={{ ...DragMatchingImageStyles.textContainer, fontFamily: CANVAS_DEFAULT_FONT_FAMILY }}
        className="outline-none"
        onMouseDown={(e) => {
          if (!isLiveMode) e.currentTarget.focus();
        }}
        onBlur={(e) => {
          dispatch(updateElementProps({ id: element.id, props: { text: e.target.innerText } }));
        }}
      >
        {element.text}
      </div>

      <div
        style={{
          ...DragMatchingImageStyles.imageChoicesContainer,
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: "0.75rem",
        }}
      >
        {element?.imageChoices?.map((item, index) =>
          isLiveMode ? (
            <div
              key={`${item.media.filename}-${item.isCorrectAnswer}-${index}`}
              style={{
                ...DragMatchingImageStyles.imageContainer,
                ...gradedRingStyle(
                  gradeChoice(item.isCorrectAnswer, selectedImageId === `${index}`, isFrozen)
                ),
              }}
            >
              {item.media.src ? (
                <img
                  draggable={false}
                  src={item.media.src}
                  alt={item.media.filename || "choice image"}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <div>
                  <AiFillPicture className="text-3xl text-slate-400" />
                </div>
              )}
            </div>
          ) : (
            <DroppableZone
              key={`${item.media.filename}-${item.isCorrectAnswer}-${index}`}
              id={`${element.id}-image-choice-${index}`}
              data={{
                id: `question//dragMatchingImage//${element.id}//${index}`,
                type: "question-dragMatchingText",
              }}
              allowedType={["image"]}
              style={DragMatchingImageStyles.imageContainer}
              onLocalFileDrop={(file) =>
                handleFileDrop(file, `question//dragMatchingImage//${element.id}//${index}`)
              }
            >
              <ChoiceButtons
                handleToggleIsCorrectAnswer={handleToggleIsCorrectAnswer}
                index={index}
                isCorrectAnswer={item.isCorrectAnswer}
                isShown={true}
                handleRemoveChoice={() => {}}
                position="2"
              />

              {item.media.src ? (
                <img
                  draggable={false}
                  src={item.media.src}
                  alt={item.media.filename || "choice image"}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <div>
                  <AiFillPicture className="text-3xl text-slate-400" />
                </div>
              )}
            </DroppableZone>
          )
        )}
      </div>
    </div>
  );
}
